import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, stores as storesApi, products as productsApi, orders as ordersApi, tenders as tendersApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Plus, Trash2, Package, Store as StoreIcon, IndianRupee, Boxes, Pencil, BookOpen, TrendingUp, ShieldCheck, MapPin, Gavel, Wallet, Siren, Mic, ScrollText, Truck, Bike, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploader from '../components/ImageUploader';
import EditStoreDialog from '../components/EditStoreDialog';
import OwnerTendersPanel from '../components/OwnerTendersPanel';
import KhataBook from '../components/KhataBook';
import BusinessWidget from '../components/BusinessWidget';
import OwnerKycForm from '../components/OwnerKycForm';
import ShopLocationPhotos from '../components/ShopLocationPhotos';
import PayoutDashboard from '../components/PayoutDashboard';
import KhataManagement from '../components/KhataManagement';
import MasterCatalogGrid from '../components/MasterCatalogGrid';
import StoreLicenseUploader from '../components/StoreLicenseUploader';
import OwnerEmergencyAlerts from '../components/OwnerEmergencyAlerts';
import OwnerVoiceOrders from '../components/OwnerVoiceOrders';
import MerchantComplianceTab from '../components/MerchantComplianceTab';
import MerchantQuickLaunch from '../components/MerchantQuickLaunch';
import MerchantTutorialVideo from '../components/MerchantTutorialVideo';
import LiveClock from '../components/LiveClock';
import { SellerTaxStatement } from '../components/TaxStatements';
import SellerScorecard from '../components/SellerScorecard';
import SellerCatalogTab from '../components/SellerCatalogTab';

const TABS = [
  { id: 'inventory', label: 'Inventory & Orders', Icon: Boxes },
  { id: 'scorecard', label: 'My Scorecard', Icon: TrendingUp },
  { id: 'national', label: 'Sell across India', Icon: Sparkles },
  { id: 'tenders', label: 'Live tenders', Icon: Gavel },
  { id: 'sos', label: 'SOS alerts', Icon: Siren },
  { id: 'voice', label: 'New Voice Orders', Icon: Mic },
  { id: 'compliance', label: 'Legal & Vault', Icon: ScrollText },
  { id: 'payouts', label: 'Payouts (₹)', Icon: Wallet },
  { id: 'tax', label: 'Tax statement', Icon: ScrollText },
  { id: 'business', label: 'Business (₹)', Icon: TrendingUp },
  { id: 'khata', label: 'Digital Khata (Udhaar)', Icon: BookOpen },
  { id: 'location', label: 'Location & Photos', Icon: MapPin },
  { id: 'kyc', label: 'KYC & Bank', Icon: ShieldCheck },
];

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [myStores, setMyStores] = useState([]);
  const [activeStoreId, setActiveStoreId] = useState(null);
  const [storeData, setStoreData] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [openTenders, setOpenTenders] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [newProd, setNewProd] = useState({ name: '', price: '', unit: 'kg', stock: 50, image_url: '', important: false, costly: false, use_by_date: '', mfg_date: '', batch_no: '' });
  const [tab, setTab] = useState('inventory');

  const refreshTenders = async () => {
    try {
      const next = await tendersApi.forOwner();
      setOpenTenders(prev => {
        // Notify owner of brand-new tenders
        const prevIds = new Set(prev.map(t => t.id));
        const fresh = next.filter(t => !prevIds.has(t.id));
        if (prev.length && fresh.length) {
          fresh.forEach(t => toast(`New quote request: ${t.product_name}`, { description: `${t.quantity} ${t.unit} · ${t.village}`, duration: 6000 }));
        }
        return next;
      });
    } catch (e) { console.warn('[dashboard] refreshTenders failed', e?.response?.status); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/'); return; }
    storesApi.myStores().then(s => {
      setMyStores(s);
      if (s.length) setActiveStoreId(s[0].id);
    });
    ordersApi.forOwner().then(setAllOrders);
    refreshTenders();
    const poll = setInterval(refreshTenders, 8000);
    return () => clearInterval(poll);
  }, [user, loading, nav]);

  useEffect(() => {
    if (activeStoreId) storesApi.get(activeStoreId).then(setStoreData);
  }, [activeStoreId]);

  const addProduct = async () => {
    if (!newProd.name || !newProd.price) { toast.error('Name & price required'); return; }
    try {
      const payload = { ...newProd, price: parseFloat(newProd.price), stock: parseInt(newProd.stock) || 0 };
      // Strip blank optional dates so backend doesn't try to parse ""
      if (!payload.use_by_date) delete payload.use_by_date;
      if (!payload.mfg_date) delete payload.mfg_date;
      if (!payload.batch_no) delete payload.batch_no;
      await productsApi.add(activeStoreId, payload);
      setNewProd({ name: '', price: '', unit: 'kg', stock: 50, image_url: '', important: false, costly: false, use_by_date: '', mfg_date: '', batch_no: '' });
      const data = await storesApi.get(activeStoreId);
      setStoreData(data);
      toast.success('Product added');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not add product');
    }
  };

  const removeProduct = async (pid) => {
    await productsApi.remove(pid);
    const data = await storesApi.get(activeStoreId);
    setStoreData(data);
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink/60">Loading…</div>;
  if (!user) return null;

  const ordersForStore = allOrders.filter(o => o.store_id === activeStoreId);
  const revenue = ordersForStore.filter(o => o.payment_status === 'paid').reduce((s, o) => s + o.total, 0);
  const productsCount = storeData?.products?.length || 0;

  if (myStores.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <StoreIcon className="w-12 h-12 mx-auto text-ink/30"/>
        <h1 className="font-heading font-black text-3xl mt-4">No store yet</h1>
        <p className="text-ink/60 mt-2">Set up your store to start receiving orders from your village.</p>
        <Link to="/become-seller" data-testid="open-store-link" className="btn-primary mt-6 inline-flex">Open my store</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400] flex items-center gap-2">
            Owner dashboard
            {user.serial_id && <span data-testid="owner-serial-id" className="rounded-full bg-[#1B5E20] text-white px-2 py-0.5 text-[10px] font-mono font-bold tracking-wider">{user.serial_id}</span>}
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">Welcome back, {user.name}</h1>
          <LiveClock testid="dashboard-live-clock" className="mt-1 text-xs font-bold text-ink/60"/>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {myStores.map(s => (
            <button key={s.id} data-testid={`switch-store-${s.id}`} onClick={() => setActiveStoreId(s.id)} className={`pill ${activeStoreId===s.id?'!bg-ink !text-white':'!bg-white'}`}>{s.name}</button>
          ))}
          <Link to="/supply-chain" data-testid="dash-supply-chain-link" className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]"><Truck className="w-3 h-3"/>Supply chain</Link>
          <Link to="/fleet" data-testid="dash-fleet-link" className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]"><Bike className="w-3 h-3"/>Delivery fleet</Link>
          <Link to="/ai-buy" data-testid="dash-ai-buy-link" className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]"><TrendingUp className="w-3 h-3"/>AI Buy</Link>
          <Link to="/become-seller" className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]"><Plus className="w-3 h-3"/>New store</Link>
          {storeData?.store && (
            <button data-testid="edit-store-button" onClick={() => setEditOpen(true)} className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]"><Pencil className="w-3 h-3"/>Edit profile</button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Stat label="Orders" value={ordersForStore.length} Icon={Package} color="text-[#0D47A1] bg-[#E3F2FD]"/>
        <Stat label="Products" value={productsCount} Icon={Boxes} color="text-[#9A7B0A] bg-[#FFF8E1]"/>
        <Stat label="Revenue" value={`₹${revenue.toFixed(0)}`} Icon={IndianRupee} color="text-[#1B5E20] bg-[#E8F5E9]"/>
      </div>

      <MerchantTutorialVideo/>
      <MerchantQuickLaunch storeId={activeStoreId} onChanged={() => storesApi.get(activeStoreId).then(setStoreData)}/>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 flex-wrap border-b border-border pb-1">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} data-testid={`dash-tab-${id}`} onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-sm font-semibold border-b-2 transition ${
              tab === id ? 'border-[#E67E22] text-[#D35400] bg-[#FFF8EE]' : 'border-transparent text-ink/60 hover:text-[#D35400]'
            }`}>
            <Icon className="w-4 h-4"/>{label}
          </button>
        ))}
      </div>

      {tab === 'tenders' && storeData?.store && (
        <div className="mt-5">
          <OwnerTendersPanel tenders={openTenders} store={storeData.store} onRefresh={refreshTenders}/>
        </div>
      )}

      {tab === 'sos' && (
        <div className="mt-5">
          <OwnerEmergencyAlerts/>
        </div>
      )}

      {tab === 'voice' && (
        <div className="mt-5">
          <OwnerVoiceOrders/>
        </div>
      )}

      {tab === 'compliance' && (
        <div className="mt-5">
          <MerchantComplianceTab
            store={storeData?.store}
            onSaved={() => storesApi.get(activeStoreId).then(setStoreData)}
          />
        </div>
      )}

      {tab === 'business' && <div className="mt-5"><BusinessWidget/></div>}
      {tab === 'payouts' && <div className="mt-5"><PayoutDashboard/></div>}
      {tab === 'tax' && <div className="mt-5"><SellerTaxStatement/></div>}
      {tab === 'scorecard' && <SellerScorecard/>}
      {tab === 'national' && <SellerCatalogTab/>}
      {tab === 'khata' && <div className="mt-5"><KhataManagement/></div>}
      {tab === 'kyc' && <div className="mt-5"><OwnerKycForm/></div>}
      {tab === 'location' && (
        <div className="mt-5">
          <StoreLicenseUploader
            store={storeData?.store}
            onSaved={() => storesApi.get(activeStoreId).then(setStoreData)}
          />
          <ShopLocationPhotos store={storeData?.store} onSaved={() => storesApi.get(activeStoreId).then(setStoreData)}/>
        </div>
      )}

      {tab === 'inventory' && (
      <>
        <MasterCatalogGrid
          storeId={storeData?.store?.id}
          shopType={storeData?.store?.category === 'medical' ? 'medical' : 'kirana'}
          onAdded={() => storesApi.get(activeStoreId).then(setStoreData)}
        />
      <div className="grid lg:grid-cols-[1fr_1fr] gap-6 mt-6">
        {/* Inventory */}
        <section className="card-soft p-5">
          <div className="font-heading font-bold text-lg">Inventory</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
            <input data-testid="prod-name" placeholder="Product name" value={newProd.name} onChange={e=>setNewProd({...newProd, name:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2 text-sm md:col-span-2"/>
            <input data-testid="prod-price" placeholder="Price ₹" value={newProd.price} onChange={e=>setNewProd({...newProd, price:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
            <select data-testid="prod-unit" value={newProd.unit} onChange={e=>setNewProd({...newProd, unit:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
              <option value="kg">per kg</option><option value="pc">per piece</option><option value="litre">per litre</option><option value="packet">per packet</option>
            </select>
            <input data-testid="prod-stock" placeholder="Stock" value={newProd.stock} onChange={e=>setNewProd({...newProd, stock:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
            <div className="md:col-span-2">
              <ImageUploader testId="prod-image-uploader" label="product image" value={newProd.image_url} onChange={(url) => setNewProd({...newProd, image_url: url})}/>
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-4 text-xs">
              <label data-testid="prod-flag-important" className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={newProd.important} onChange={e=>setNewProd({...newProd, important:e.target.checked})} className="accent-[#1B5E20]"/>
                <span><b>Important goods</b> — OTP-on-delivery for this item</span>
              </label>
              <label data-testid="prod-flag-costly" className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={newProd.costly} onChange={e=>setNewProd({...newProd, costly:e.target.checked})} className="accent-[#7A4A00]"/>
                <span><b>Costly goods</b> — video proof required on return</span>
              </label>
            </div>

            {/* Expiry / Use-by — MANDATORY for food / dairy / kirana / bakery / medical / hotels */}
            <div className="md:col-span-2 rounded-lg border border-[#1B5E20]/15 bg-[#E8F5E9]/40 p-3" data-testid="prod-expiry-block">
              <div className="text-[11px] font-bold text-[#1B5E20] uppercase tracking-wider">
                Expiry & batch (mandatory for food / consumables / medical)
              </div>
              <p className="text-[11px] text-ink/60 mt-0.5 mb-2">FSSAI 2.2.1 + Drug &amp; Cosmetic Act — sellers must print Use-by &amp; MFG date on every consumable.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <label className="text-xs text-ink/70">
                  Use-by date <span className="text-[#C62828]">*</span>
                  <input type="date" data-testid="prod-use-by" value={newProd.use_by_date} onChange={e=>setNewProd({...newProd, use_by_date:e.target.value})} className="mt-1 w-full rounded-lg border border-border bg-white px-2 py-1.5 text-sm"/>
                </label>
                <label className="text-xs text-ink/70">
                  Manufacturing date
                  <input type="date" data-testid="prod-mfg-date" value={newProd.mfg_date} onChange={e=>setNewProd({...newProd, mfg_date:e.target.value})} className="mt-1 w-full rounded-lg border border-border bg-white px-2 py-1.5 text-sm"/>
                </label>
                <label className="text-xs text-ink/70">
                  Batch / lot no.
                  <input type="text" data-testid="prod-batch-no" value={newProd.batch_no} onChange={e=>setNewProd({...newProd, batch_no:e.target.value})} placeholder="e.g. B240315" className="mt-1 w-full rounded-lg border border-border bg-white px-2 py-1.5 text-sm"/>
                </label>
              </div>
            </div>

            <button data-testid="add-product-button" onClick={addProduct} className="btn-primary md:col-span-2"><Plus className="w-4 h-4"/>Add product</button>
          </div>

          <div className="mt-5 space-y-2 max-h-96 overflow-auto pr-1">
            {storeData?.products?.map(p => (
              <div key={p.id} data-testid={`inv-row-${p.id}`} className="flex items-center gap-3 rounded-lg border border-border bg-white p-2">
                <div className="w-10 h-10 rounded bg-muted overflow-hidden shrink-0">{p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover"/> : <AiPictureButton product={p} onDone={() => storesApi.get(activeStoreId).then(setStoreData)}/>}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate inline-flex items-center gap-1.5">
                    {p.name}
                    {p.important && <span className="text-[9px] uppercase tracking-wider bg-[#E8F5E9] text-[#1B5E20] px-1.5 py-0.5 rounded">OTP</span>}
                    {p.costly && <span className="text-[9px] uppercase tracking-wider bg-[#FFF8E1] text-[#7A4A00] px-1.5 py-0.5 rounded">Video-return</span>}
                  </div>
                  <div className="text-xs text-ink/60">₹{p.price} / {p.unit} · Stock {p.stock}</div>
                </div>
                <button data-testid={`inv-remove-${p.id}`} onClick={() => removeProduct(p.id)} className="text-ink/40 hover:text-destructive"><Trash2 className="w-4 h-4"/></button>
              </div>
            ))}
          </div>
        </section>

        {/* Orders */}
        <section className="card-soft p-5">
          <div className="font-heading font-bold text-lg">Recent orders</div>
          <div className="mt-3 space-y-2 max-h-[480px] overflow-auto pr-1">
            {ordersForStore.length === 0 ? <div className="text-sm text-ink/60">No orders yet for this store.</div>
              : ordersForStore.map(o => (
                <Link to={`/orders/${o.id}`} key={o.id} data-testid={`owner-order-${o.id}`} className="block rounded-lg border border-border bg-white p-3 hover:border-[#E67E22] transition">
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <div className="text-sm font-semibold">{o.address.full_name}</div>
                      <div className="text-xs text-ink/60">{o.items.length} items · {new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">₹{o.total.toFixed(2)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-ink/60">{o.status.replace(/_/g,' ')}</div>
                    </div>
                  </div>
                </Link>
              ))
            }
          </div>
        </section>
      </div>
      </>
      )}
      <EditStoreDialog
        store={storeData?.store}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={(updated) => setStoreData(d => ({ ...d, store: updated }))}
      />
    </div>
  );
}

function Stat({ label, value, Icon, color }) {
  return (
    <div className="card-soft p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl grid place-items-center ${color}`}><Icon className="w-5 h-5"/></div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/50">{label}</div>
        <div className="font-heading font-black text-2xl">{value}</div>
      </div>
    </div>
  );
}

function AiPictureButton({ product, onDone }) {
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try {
      await api.post(`/owner/products/${product.id}/ai-image`);
      toast.success(`Picture created for ${product.name}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create picture'); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={`inv-ai-image-${product.id}`} onClick={make} disabled={busy} title="Create a picture with AI"
      className="w-full h-full grid place-items-center text-[#D35400] hover:bg-[#FFF3E0]">
      {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
    </button>
  );
}
